"use client";

import { TypePackageDetailWithCarousal } from "@contentful/types";
import { getYzsaId } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import { Icon } from "@yardzen/ui/components/icon";
import { Link } from "@yardzen/ui/components/link";
import { throttle } from "lodash";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";
import { hasPromoPrice, showsPromoTotal } from "./pricing";

// This is a replacement for the PackageDetailBanner component. We currently need
// both as we are A/B testing different packages and these behave differently.
export function PackageDetailFloatingCard({
  packageDetailBanner,
  splitTreatment,
  addOnTotal,
  addToCartId = "pdp-add-to-cart",
}: {
  packageDetailBanner: TypePackageDetailWithCarousal<"WITHOUT_UNRESOLVABLE_LINKS">;
  splitTreatment?: boolean;
  addOnTotal: number;
  /** Must match the addToCartId given to the paired PackageDetailItem so the
   *  observer tracks this card's own CTA when several render on one page. */
  addToCartId?: string;
}) {
  const [hostName, setHostName] = useState<string>("");
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();

  useEffect(() => {
    setHostName(window.location.origin);
  }, []);

  const { uniqueIdentifier } =
    packageDetailBanner?.fields.package?.fields || {};

  const [active, setActive] = useState(false);
  const bannerOffsetRef = useRef(0);

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + 7);

  useEffect(() => {
    // Preferred: track the hero Add to Cart — the bar shows whenever the
    // primary CTA is off screen, so a buy action is always visible.
    const heroCta = document.getElementById(addToCartId);
    if (heroCta) {
      const observer = new IntersectionObserver(
        ([entry]) => setActive(!entry.isIntersecting),
        { rootMargin: "0px" },
      );
      observer.observe(heroCta);
      return () => observer.disconnect();
    }

    // Fallback: original scroll-past-trigger behavior.
    const navElement = document.querySelector("#sub-nav-bar-trigger");
    let currentNavHeight = 0;

    if (navElement !== null && navElement instanceof HTMLElement) {
      const bound = navElement.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      currentNavHeight = scrollTop + bound.top;
    }

    let lastScrollY = window.scrollY;

    // Combined scroll handler for both nav trigger and promo banner detection
    const scrollHandler = throttle(() => {
      const currentScrollY = window.scrollY;

      // Handle nav trigger logic
      if (window.scrollY < currentNavHeight) {
        setActive(false);
      } else {
        setActive(true);
      }

      // Handle promo banner detection
      if (currentScrollY > lastScrollY || currentScrollY < lastScrollY) {
        const promoBannerElement = document.querySelector("#yz-banner");
        if (promoBannerElement) {
          bannerOffsetRef.current = promoBannerElement.clientHeight;
        }
      }

      lastScrollY = currentScrollY;
    }, 250);

    window.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [setActive, addToCartId]);

  useEffect(() => {
    // Function to check if the screen is small
    const checkIsMobile = () => {
      return window.innerWidth < 768; // You can adjust the breakpoint as needed
    };

    // Function to handle Calendly popup button positioning for mobile
    const handleCalendlyButtonPositioning = () => {
      const calendlyButton = document.querySelector("#calendly-popup-button");

      if (calendlyButton && calendlyButton instanceof HTMLElement) {
        const isMobile = checkIsMobile();
        if (isMobile && active) {
          // The bar exposes its own schedule link on mobile — hide the
          // standalone pill so two floaters never stack over content.
          calendlyButton.style.display = "none";
        } else {
          calendlyButton.style.display = "";
        }
      }
    };

    // Initial setup
    handleCalendlyButtonPositioning();

    // Handle resize events
    const handleResize = () => {
      handleCalendlyButtonPositioning();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      // The Calendly pill lives in the persistent marketing layout, so restore
      // its visibility on unmount — otherwise an SPA nav away leaves it hidden
      // everywhere until a full reload.
      const calendlyButton = document.querySelector("#calendly-popup-button");
      if (calendlyButton instanceof HTMLElement) {
        calendlyButton.style.display = "";
      }
    };
  }, [active]);

  const challengerUrl =
    packageDetailBanner.fields.package?.fields.cta?.fields.challengerURL ?? "";
  const controlUrl =
    packageDetailBanner.fields.package?.fields.cta?.fields.url ?? "";

  // If split treatment is on and challenger URL is defined, use the challenger URL.
  const fallbackUrl = new URL("https://yardzen.com");
  let ctaUrl: URL = fallbackUrl;

  try {
    const urlToUse =
      splitTreatment && challengerUrl ? challengerUrl : controlUrl;
    if (urlToUse && urlToUse.trim() !== "") {
      const baseUrl = hostName || "https://yardzen.com";
      ctaUrl = urlToUse.startsWith("http")
        ? new URL(urlToUse)
        : new URL(urlToUse, baseUrl);
    }
  } catch (error) {
    console.warn("Invalid URL provided for CTA:", {
      challengerUrl,
      controlUrl,
      hostName,
    });
    ctaUrl = fallbackUrl;
  }

  const yzsaCookie = getYzsaId();

  // Add yzsa parameter to url like wordpress was doing.
  if (ctaUrl && yzsaCookie && !ctaUrl.searchParams.has("yzsa")) {
    ctaUrl.searchParams.set("yzsa", encodeURIComponent(yzsaCookie));
  }

  // Checkout page requires extra packageDetail param
  if (ctaUrl && uniqueIdentifier && !ctaUrl.searchParams.has("packageDetail")) {
    ctaUrl.searchParams.set(
      "packageDetail",
      encodeURIComponent(uniqueIdentifier),
    );
  }
  const thumbnailImage =
    packageDetailBanner.fields.carousal?.fields.images?.[0]?.fields.image
      ?.fields.file?.url;

  return (
    <div id="sub-nav-bar-trigger">
      <nav
        id="sub-nav-bar"
        style={{ bottom: "0px" }}
        className={`bg-system-grayscale-00 fixed z-50 mt-3 w-full justify-center bg-opacity-80 p-4 shadow-xl backdrop-blur-[64px] md:mx-4 md:mb-4 md:mt-2 md:w-auto md:rounded-lg ${!active ? "hidden" : ""}`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex-0 hidden h-16 w-16 md:block ">
              {thumbnailImage ? (
                <Image
                  src={`https:${thumbnailImage}`}
                  alt={
                    packageDetailBanner.fields.package?.fields.heading ??
                    "Package Image"
                  }
                  width={64}
                  height={64}
                  className="h-full w-full rounded-md object-cover"
                />
              ) : (
                <div className="bg-brand-tertiary-30 flex h-16 w-16 items-center justify-center rounded-md">
                  {" "}
                  YZ{" "}
                </div>
              )}
            </div>
            <div className="flex w-full flex-1 flex-col text-left">
              <div className="flex flex-col">
                <span className="text-md text-nowrap font-semibold">
                  {packageDetailBanner.fields.package?.fields.heading}
                </span>
              </div>

              <div className="flex w-full basis-1 flex-row items-center">
                {hasPromoPrice(
                  packageDetailBanner.fields.package?.fields.price,
                  packageDetailBanner.fields.package?.fields.promoPrice,
                ) ? (
                  <>
                    <p className="text-system-grayscale-50 ">
                      <span className="pr-2 line-through">
                        $
                        {packageDetailBanner.fields.package?.fields.price ??
                          0 + addOnTotal}
                      </span>
                    </p>
                    {showsPromoTotal(
                      (packageDetailBanner.fields.package?.fields.promoPrice ??
                        0) + addOnTotal,
                    ) && (
                      <div>
                        $
                        {Number(
                          (packageDetailBanner.fields.package?.fields
                            .promoPrice ?? 0) + addOnTotal,
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    $
                    {Number(
                      (packageDetailBanner.fields.package?.fields.price ?? 0) +
                        addOnTotal,
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Button className="mx-2 w-full" asChild>
                <Link
                  className="m-0 whitespace-nowrap font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
                  href={withBuilderPartnerParams(
                    hostName
                      ? ctaUrl.toString()
                      : ctaUrl.pathname + ctaUrl.search ||
                          "https://yardzen.com",
                  )}
                >
                  {packageDetailBanner.fields.package?.fields.cta?.fields
                    .buttonText || "Buy Now"}
                  <Icon name="arrow-right" />
                </Link>
              </Button>
              {/* Mobile: the schedule action lives here as a quiet link so
                  only one floating bar ever covers the page. */}
              <button
                type="button"
                onClick={() =>
                  (
                    document.querySelector(
                      "#calendly-popup-button",
                    ) as HTMLElement | null
                  )?.click()
                }
                className="text-brand-primary-darker/70 mt-1 block w-full text-center text-xs underline underline-offset-2 md:hidden"
              >
                or schedule a free call
              </button>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
